import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../commonComponents/header/Header.js";
import { apiService } from "../../../services/apiService";
import ConfirmDialog from "../../commonComponents/ConfirmDialog.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const generateAllSlots = () => {
  const slots = [];
  for (let i = 0; i < 48; i++) {
    const totalMinutes = i * 30;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const period = hours < 12 ? "AM" : "PM";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const timeStr = `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
    slots.push({ id: i + 1, time: timeStr, isAM: hours < 12 });
  }
  return slots;
};

const ALL_SLOTS = generateAllSlots();
const AM_SLOTS = ALL_SLOTS.filter((s) => s.isAM);
const PM_SLOTS = ALL_SLOTS.filter((s) => !s.isAM);

const formatDateLabel = (date) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

// "12 May 2026" — used in the confirm dialog title
const formatDateShort = (date) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// const buildDateRange = (endDate) => {
//   const today = new Date();
//   today.setHours(0, 0, 0, 0);
//   const end = new Date(endDate);
//   end.setHours(0, 0, 0, 0);
//   const dates = [];
//   const cur = new Date(today);
//   while (cur <= end) {
//     dates.push(new Date(cur));
//     cur.setDate(cur.getDate() + 1);
//   }
//   return dates;
// };

const buildDateRange = (endDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  const start = new Date(end);
  start.setDate(start.getDate() - 45);

  // If endDate - 45 is before today, use today
  if (start < today) {
    start.setTime(today.getTime());
  }

  const dates = [];
  const cur = new Date(start);

  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }

  return dates;
};

const formatClosingDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-GB", { month: "short" });
  return `${day}-${month}-${d.getFullYear()}`;
};

const daysRemaining = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(dateStr);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / 86400000);
};

// Derive "Morning / Afternoon / Evening / Night" label from a time string like "09:30 AM"
const getTimeOfDay = (timeStr) => {
  const [timePart, period] = timeStr.split(" ");
  const [h] = timePart.split(":").map(Number);
  const hour24 =
    period === "PM" && h !== 12 ? h + 12 : period === "AM" && h === 12 ? 0 : h;
  if (hour24 >= 5 && hour24 < 12) return "Morning";
  if (hour24 >= 12 && hour24 < 17) return "Afternoon";
  if (hour24 >= 17 && hour24 < 21) return "Evening";
  return "Night";
};

// Returns true if the slot falls at or before (now + 30 min) on today's date.
// For any future date, always returns false.
const isPastSlot = (slot, selectedDate) => {
  const today = new Date();
  const selDate = new Date(selectedDate);
  // Compare calendar dates only
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const selKey = `${selDate.getFullYear()}-${selDate.getMonth()}-${selDate.getDate()}`;
  if (selKey !== todayKey) return false;

  // Parse slot time string e.g. "12:30 AM" / "01:00 PM"
  const [timePart, period] = slot.time.split(" ");
  const [h, m] = timePart.split(":").map(Number);
  const hour24 =
    period === "PM" && h !== 12 ? h + 12 : period === "AM" && h === 12 ? 0 : h;

  const slotDate = new Date(today);
  slotDate.setHours(hour24, m, 0, 0);

  // Slot is past/unavailable if it starts within the next 30 minutes or already passed
  const cutoff = new Date(today.getTime() + 15 * 60 * 1000);
  return slotDate <= cutoff;
};

const SLOTS_PER_ROW = 12;
const SLOT_GAP = 10;

// ─── Top Snackbar ─────────────────────────────────────────────────────────────
const TopSnackbar = ({ message, type = "error", onClose }) => {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [message, onClose]);

  if (!message) return null;

  const bg =
    type === "success" ? "#098817" : type === "warning" ? "#f66e0e" : "#DC2626";
  const icon =
    type === "success" ? (
      // ✓ circle
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="8.25" stroke="#fff" strokeWidth="1.5" />
        <path
          d="M5 9l3 3 5-5"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ) : type === "warning" ? (
      // ⚠ triangle
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2L16.5 15H1.5L9 2Z"
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M9 7v4M9 12.5v.5"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    ) : (
      // ! circle
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="8.25" stroke="#fff" strokeWidth="1.5" />
        <path
          d="M9 5v5M9 12.5v.5"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );

  return (
    <div
      style={{
        position: "fixed",
        top: "16px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2000,
        background: bg,
        color: "#fff",
        borderRadius: "6px",
        padding: "12px 18px",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        maxWidth: "520px",
        width: "calc(100% - 32px)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        animation: "sbSlideIn 0.22s ease",
      }}
    >
      <span style={{ flexShrink: 0, marginTop: "1px" }}>{icon}</span>
      <span style={{ fontSize: "13px", lineHeight: 1.5, flex: 1 }}>
        {message}
      </span>
      <button
        type="button"
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          color: "#fff",
          cursor: "pointer",
          padding: "0 2px",
          lineHeight: 1,
          flexShrink: 0,
          opacity: 0.8,
        }}
        aria-label="Dismiss"
      >
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path
            d="M1 1l9 9M10 1L1 10"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
};

// ─── Tooltip wrapper ──────────────────────────────────────────────────────────
const Tooltip = ({ text, children }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div
      style={{ position: "relative", display: "inline-block", width: "100%" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#111729",
            color: "#fff",
            fontSize: "11px",
            padding: "4px 8px",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 500,
          }}
        >
          {text}
          {/* Arrow */}
          <span
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid #111729",
            }}
          />
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SlotBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nd = location.state || {};

  const FONT_SIZE = { primary: "24px", secondary: "16px", tertiary: "13px" };
  const BORDER_RADIUS = "4px";

  const {
    jobTitle = "Senior Verification Engineer",
    companyName = "ABC Technology Pvt. Ltd.",
    lastDate = "2026-06-01",
    candidateName = null,
    resumeId = null,
    jobDescriptionId = null,
    interviewId = null,
    timeDuration = 1800,
    timezone = "Asia/India/Calcutta",
    isAssessment = false,
    from = "screen-up",
  } = nd;

  const dateRange = useMemo(() => buildDateRange(lastDate), [lastDate]);

  const [selectedDate, setSelectedDate] = useState(dateRange[0] || new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Confirm dialog
  const [showConfirm, setShowConfirm] = useState(false);

  // Top snackbar
  const [snackbar, setSnackbar] = useState({ message: "", type: "error" });
  const dismissSnackbar = useCallback(
    () => setSnackbar({ message: "", type: "error" }),
    [],
  );
  const showSnackbar = useCallback(
    (message, type = "error") => setSnackbar({ message, type }),
    [],
  );

  // Check sessionStorage on mount for expired access reason
  useEffect(() => {
    const accessReason = sessionStorage.getItem("accessReason");
    if (accessReason === "expired") {
      const accessMessage =
        sessionStorage.getItem("accessMessage") || "Your session has expired.";
      showSnackbar(
        `${accessMessage} You can book another slot and take the assessment.`,
        "warning",
      );
    }
  }, []);

  // API-driven slot state
  const [fullSlotIndices, setFullSlotIndices] = useState(new Set());
  const [slotIdMap, setSlotIdMap] = useState({}); // slot_index (0-based) → slot_id
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  const selectedDateKey = formatDateKey(selectedDate);

  const loadSlots = useCallback(async (dateKey) => {
    setSlotsLoading(true);
    setSlotsError(null);
    try {
      const slots = await apiService.getSlotsByDate(dateKey);
      const fullIndices = new Set();
      const idMap = {};
      slots.forEach((slot) => {
        idMap[slot.slot_index] = slot.slot_id;
        if (slot.is_full) fullIndices.add(slot.slot_index);
      });
      setFullSlotIndices(fullIndices);
      setSlotIdMap(idMap);
    } catch (err) {
      console.error("Failed to load slot data:", err);
      setSlotsError("Could not load slot availability. Please try again.");
      setFullSlotIndices(new Set());
      setSlotIdMap({});
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlots(selectedDateKey);
  }, [selectedDateKey, loadSlots]);

  const slotStatus = (id) => {
    const slot = ALL_SLOTS.find((s) => s.id === id);
    if (slot && isPastSlot(slot, selectedDate)) return "past";
    if (selectedSlot?.id === id) return "selected";
    if (fullSlotIndices.has(id - 1)) return "full";
    return "available";
  };

  const handleSlotClick = (slot) => {
    if (
      bookingSuccess ||
      slotsLoading ||
      slotStatus(slot.id) === "full" ||
      slotStatus(slot.id) === "past"
    )
      return;
    // Toggle: clicking selected slot deselects it
    if (selectedSlot?.id === slot.id) {
      setSelectedSlot(null);
      return;
    }
    const slot_id = slotIdMap[slot.id - 1];
    setSelectedSlot({ ...slot, slot_id });
  };

  // Called when user clicks "Book Slot" — shows confirm dialog
  const handleBookSlotClick = () => {
    if (!selectedSlot) {
      showSnackbar("Please select a time slot before booking.", "error");
      return;
    }
    setShowConfirm(true);
  };

  // Called when user confirms in dialog
  const handleConfirmBooking = async () => {
    const slotId = selectedSlot.slot_id ?? selectedSlot.id - 1;
    setIsBooking(true);
    try {
      let response;
      // if (from === "slot-booking-otp") {
      response = await apiService.bookSlotDirect({
        slotId,
        isAssessment,
        duration: timeDuration,
      });
      // } else
      //   response = await apiService.bookSlot({
      //     slotId,
      //     interviewId,
      //     duration: timeDuration,
      //     isAssessment,
      //     campaignType: "campaign_builder",
      //   });

      if (!response?.success) {
        setShowConfirm(false);

        showSnackbar(response?.message || "Failed to book the slot.", "error");

        return; // stop here, do NOT redirect
      }

      // Success case
      setShowConfirm(false);
      setBookingSuccess(true);

      showSnackbar(
        `Assessment Slot for ${formatDateShort(selectedDate)}, ${
          selectedSlot.time
        } (${getTimeOfDay(
          selectedSlot.time,
        )}) Booked Successfully. You will receive an email confirmation and a link to the assessment shortly.`,
        "success",
      );

      setTimeout(() => {
        navigate("/welcome-response", {
          state: {
            resumeId,
            jobDescriptionId,
            lastDate,
            candidateName,
            bookedSlot: {
              date: selectedDateKey,
              slot: selectedSlot,
            },
          },
        });
      }, 5000);
    } catch (err) {
      setShowConfirm(false);

      let errorMessage = "Failed to book the slot. Please try again.";

      const detail = err?.detail || err?.response?.detail || err?.data?.detail;

      if (Array.isArray(detail)) {
        errorMessage = detail
          .map((d) => d?.msg)
          .filter(Boolean)
          .join("; ");
      } else if (typeof detail === "string") {
        errorMessage = detail;
      } else if (err?.message && err.message !== "API Error") {
        errorMessage = err.message;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsBooking(false);
    }
  };

  // ── Slot button ──────────────────────────────────────────────────────────
  const SlotButton = ({ slot }) => {
    const status = slotStatus(slot.id);
    const isSelected = status === "selected";
    const isFull = status === "full";
    const isPast = status === "past";
    const isDisabled = isFull || isPast || bookingSuccess;

    const base = {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "75px",
      borderRadius: BORDER_RADIUS,
      border: "1px solid",
      cursor: isDisabled ? "not-allowed" : "pointer",
      transition: "border-color 0.1s, background 0.1s",
      userSelect: "none",
    };

    const theme = isSelected
      ? {
          borderColor: "#0AA210",
          borderWidth: "2px",
          background: "#C1F8C3",
          color: "#111729",
        }
      : isFull
        ? { borderColor: "#999999", background: "#E2E2E2", color: "#111729" }
        : isPast
          ? {
              borderColor: "#BBBBBB",
              background: "#F0F0F0",
              color: "#AAAAAA",
              opacity: 0.65,
            }
          : { borderColor: "#0AA210", background: "#fff", color: "#111729" };

    const tooltipText = isPast
      ? "Cannot book a past time slot"
      : isFull
        ? "Slot unavailable"
        : isSelected
          ? "Click to unselect"
          : "Click to select";

    return (
      <Tooltip text={tooltipText}>
        <button
          type="button"
          onClick={() => handleSlotClick(slot)}
          disabled={isDisabled}
          style={{ ...base, ...theme }}
        >
          <span
            style={{
              fontSize: FONT_SIZE.tertiary,
              lineHeight: 1,
              marginBottom: "3px",
              color: isPast ? "#AAAAAA" : "#111729",
            }}
          >
            {String(slot.id).padStart(2, "0")}
          </span>
          <span
            style={{
              fontSize: FONT_SIZE.tertiary,
              fontWeight: 400,
              lineHeight: 1,
              color: isPast ? "#AAAAAA" : "#111729",
            }}
          >
            {slot.time}
          </span>
        </button>
      </Tooltip>
    );
  };

  const SlotGrid = ({ slots }) => (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-[10px]">
      {slots.map((slot) => (
        <SlotButton key={slot.id} slot={slot} />
      ))}
    </div>
  );

  const closingLabel = formatClosingDate(lastDate);
  const days = daysRemaining(lastDate);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#F9FAFB",
        paddingTop: "56px",
      }}
    >
      {/* ── Top Snackbar ── */}
      <TopSnackbar
        message={snackbar.message}
        type={snackbar.type}
        onClose={dismissSnackbar}
      />

      {/* ── Confirm Dialog ── */}
      <ConfirmDialog
        isOpen={showConfirm}
        title={formatDateShort(selectedDate)}
        subtitle={
          selectedSlot
            ? `${selectedSlot.time} (${getTimeOfDay(selectedSlot.time)})`
            : ""
        }
        message="Are you sure you want to book this slot?"
        onConfirm={handleConfirmBooking}
        onCancel={() => !isBooking && setShowConfirm(false)}
        isLoading={isBooking}
        confirmLabel="Yes"
        cancelLabel="No"
      />

      {/* ── Top logo header ── */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          padding: "16px",
          background: "#F9FAFB",
          zIndex: 1000,
          boxSizing: "border-box",
        }}
      >
        <Header />
      </div>

      {/* ── Grey sub-header band ── */}
      <div
        className="flex flex-col md:flex-row gap-3 md:gap-0 items-start md:items-center justify-between p-4"
        style={{
          background: "#ECEEF0",
          border: "0.25px solid #CCCCCC",
        }}
      >
        {/* Left: job info */}
        <div>
          <p
            style={{
              fontSize: FONT_SIZE.primary,
              fontWeight: 600,
              color: "#111729",
              lineHeight: 1.3,
              textTransform: "capitalize",
            }}
          >
            {jobTitle}
          </p>
          <p
            style={{
              fontSize: FONT_SIZE.primary,
              fontWeight: 600,
              color: "#111729",
              marginTop: "2px",
            }}
          >
            {companyName}
          </p>
        </div>

        {/* Centre: closing pill */}
        <div
          className="w-full md:w-auto text-center"
          style={{
            border: "0.25px solid #CCCCCC",
            borderRadius: "9999px",
            background: "#fff",
            padding: "10px 36px",
          }}
        >
          <p
            style={{
              fontSize: FONT_SIZE.primary,
              fontWeight: 600,
              color: "#111729",
            }}
          >
            Opportunity closes on {closingLabel}
          </p>
          <p
            style={{
              fontSize: FONT_SIZE.secondary,
              color: "#111729",
              marginTop: "2px",
              fontWeight: 500,
            }}
          >
            {days > 0 ? `${days} Days to go` : "Closing today"}
          </p>
        </div>

        {/* Right spacer */}
        <div className="hidden md:block" style={{ width: "220px" }} />
      </div>

      {/* ── Date selector + selected slot indicator ── */}
      <div
        className="w-full max-w-[1160px] p-4 md:px-4 md:pt-4 md:pb-0 flex flex-col md:flex-row items-start justify-between gap-4"
        style={{
          boxSizing: "border-box",
        }}
      >
        {/* Section 1 — date picker + timezone */}
        <div className="shrink-0 w-full md:w-auto">
          <h2
            style={{
              fontSize: FONT_SIZE.primary,
              fontWeight: 600,
              color: "#111729",
              margin: 0,
            }}
          >
            Schedule your assessment slot.
          </h2>
          <p
            style={{
              fontSize: FONT_SIZE.tertiary,
              color: "#111729",
              marginBottom: "10px",
            }}
          >
            Select a date of your choice and pick a slot that is available.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <div className="relative w-full sm:w-[250px]">
              <select
                value={formatDateKey(selectedDate)}
                onChange={(e) => {
                  const found = dateRange.find(
                    (d) => formatDateKey(d) === e.target.value,
                  );
                  if (found) {
                    setSelectedDate(found);
                    setSelectedSlot(null);
                  }
                }}
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  width: "100%",
                  border: "0.25px solid #666666",
                  borderRadius: BORDER_RADIUS,
                  padding: "7px 34px 7px 12px",
                  color: "#111729",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: FONT_SIZE.tertiary,
                }}
              >
                {dateRange.map((d) => (
                  <option key={formatDateKey(d)} value={formatDateKey(d)}>
                    {formatDateLabel(d)}
                  </option>
                ))}
              </select>
              <span
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "#666666",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12.219"
                  height="6.986"
                  viewBox="0 0 12.219 6.986"
                >
                  <path
                    d="M12.3,16.127l4.62-4.624a.87.87,0,0,1,1.233,0,.881.881,0,0,1,0,1.237l-5.235,5.239a.872.872,0,0,1-1.2.025l-5.271-5.26a.873.873,0,0,1,1.233-1.237Z"
                    transform="translate(-6.188 -11.246)"
                  />
                </svg>
              </span>
            </div>

            {/* Timezone pill */}
            <div
              style={{
                border: "0.25px solid #999999",
                borderRadius: "9999px",
                padding: "3px 12px",
                color: "#111729",
                width: "fit-content",
                backgroundColor: "#EEEEEE",
                fontSize: FONT_SIZE.tertiary,
              }}
            >
              {timezone}
            </div>
          </div>
        </div>

        {/* Section 2 — selected date + slot indicator */}
        <div className="w-full flex-1 flex flex-col justify-center items-start md:items-center gap-2 md:gap-2.5">
          <p
            style={{
              fontSize: FONT_SIZE.secondary,
              fontWeight: 600,
              color: "#111729",
              margin: 0,
            }}
          >
            Selected Date:{" "}
            <span style={{ fontWeight: 700 }}>
              {formatDateLabel(selectedDate)}
            </span>
          </p>
          {selectedSlot && (
            <p
              className="break-words max-w-full"
              style={{
                fontSize: FONT_SIZE.secondary,
                fontWeight: 600,
                color: "#111729",
                marginBottom: "16px",
                border: "0.25px solid #0AA210",
                backgroundColor: "#C1F8C3",
                padding: "6px 15px",
                borderRadius: "99999px",
              }}
            >
              Selected Slot:{" "}
              <span style={{ fontWeight: 600 }}>{selectedSlot.time}</span> on{" "}
              {formatDateLabel(selectedDate)}
            </p>
          )}
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 w-full max-w-[1160px] mx-auto p-4 sm:p-6 md:px-8 md:pt-6 md:pb-24 box-border">
        {/* Slot load error */}
        {slotsError && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              background: "#FEF2F2",
              border: "0.25px solid #FCA5A5",
              borderRadius: BORDER_RADIUS,
              padding: "10px 14px",
              marginBottom: "16px",
            }}
          >
            <span
              style={{
                fontSize: FONT_SIZE.tertiary,
                color: "#DC2626",
                flex: 1,
              }}
            >
              {slotsError}
            </span>
            <button
              type="button"
              onClick={() => loadSlots(selectedDateKey)}
              style={{
                fontSize: FONT_SIZE.tertiary,
                color: "#DC2626",
                background: "none",
                border: "0.25px solid #DC2626",
                borderRadius: BORDER_RADIUS,
                padding: "3px 10px",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Slot grids */}
        <div style={{ position: "relative" }}>
          {slotsLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(249,250,251,0.75)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: BORDER_RADIUS,
              }}
            >
              <span
                style={{
                  width: "22px",
                  height: "22px",
                  border: "3px solid #111729",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.6s linear infinite",
                }}
              />
            </div>
          )}

          {/* AM */}
          <div style={{ marginBottom: "45px" }}>
            <p
              style={{
                fontSize: FONT_SIZE.tertiary,
                color: "#111729",
                fontWeight: 500,
                marginBottom: "10px",
              }}
            >
              AM - 12:00 Midnight TO 11:30 AM
            </p>
            <SlotGrid slots={AM_SLOTS} />
          </div>

          {/* PM */}
          <div style={{ marginBottom: "24px" }}>
            <p
              style={{
                fontSize: FONT_SIZE.tertiary,
                color: "#111729",
                fontWeight: 500,
                marginBottom: "10px",
              }}
            >
              PM - 12:00 Noon TO 11:30 PM
            </p>
            <SlotGrid slots={PM_SLOTS} />
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-5 mt-2">
          {[
            {
              label: "Available",
              border: "#0AA210",
              bg: "#fff",
              color: "#4B5563",
            },
            {
              label: "Full",
              border: "#999999",
              bg: "#E2E2E2",
              color: "#4B5563",
            },
            {
              label: "Selected",
              border: "2px solid#0AA210",
              bg: "#C1F8C3",
              color: "#4B5563",
            },
            {
              label: "Past",
              border: "#BBBBBB",
              bg: "#F0F0F0",
              color: "#AAAAAA",
            },
          ].map(({ label, border, bg, color }) => (
            <div
              key={label}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "18px",
                  height: "18px",
                  border: `1px solid ${border}`,
                  background: bg,
                  borderRadius: BORDER_RADIUS,
                }}
              />
              <span style={{ fontSize: FONT_SIZE.tertiary, color }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Fixed bottom bar ── */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "#F9FAFB",
          borderTop: "0.25px solid #CCCCCC",
          zIndex: 30,
          display: "flex",
          alignItems: "center",
          padding: "8px 16px",
          boxSizing: "border-box",
        }}
      >
        <span className="text-[10px] sm:text-xs text-[#111729] shrink pr-2">
          ©{" "}
          {new Date().getFullYear() > 2026
            ? `2026-${new Date().getFullYear()}`
            : "2026"}
          , Needles and Haystacks Technology Pvt Ltd.
        </span>
        <div style={{ marginLeft: "auto" }}>
          <button
            type="button"
            onClick={handleBookSlotClick}
            disabled={!selectedSlot || isBooking || bookingSuccess}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "5.5px 15px",
              fontSize: FONT_SIZE.secondary,
              color: "#fff",
              background: "#111729",
              border: "none",
              borderRadius: BORDER_RADIUS,
              cursor:
                !selectedSlot || isBooking || bookingSuccess
                  ? "not-allowed"
                  : "pointer",
              opacity: !selectedSlot || isBooking || bookingSuccess ? 0.4 : 1,
              transition: "opacity 0.15s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19.275"
                height="12.75"
                viewBox="0 0 19.275 13.75"
              >
                <path
                  d="M2.421,12.833a1.382,1.382,0,0,1,1.953.033L8.8,17.451,18.993,7.381a1.382,1.382,0,0,1,1.934,1.965L9.749,20.4a1.382,1.382,0,0,1-.967.4H8.769a1.382,1.382,0,0,1-.978-.414l-5.4-5.6a1.382,1.382,0,0,1,.033-1.953Z"
                  transform="translate(-2 -7.047)"
                  fill="#fff"
                />
              </svg>
              <span>Book Slot</span>
            </div>
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes sbSlideIn { from { opacity: 0; transform: translateX(-50%) translateY(-8px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
      `}</style>
    </div>
  );
};

export default SlotBooking;
